<template>
  <div
    v-if="resultScreenVisible"
    class="tw-text-main1 tw-text-sm"
    :style="resultStyle"
  >
    <div class="tw-w-[760px]">
      <SpellingTestResult
        :student="$currentUser.value"
        :guest-name="guestName"
        :result-data="resultData"
        :test-result-info="resultInfo"
      />
    </div>
  </div>
  <div v-else-if="isGuest">
    <GuestFinalScreen
      @close="$emit('close')"
      @name-set="
        guestName = $event;
        resultScreenVisible = true;
      "
    />
  </div>
  <div v-else>
    <StudentFinalScreen
      @close="$emit('close')"
      @showresults="resultScreenVisible = true"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import GuestFinalScreen from "./GuestFinalScreen.vue";
import SpellingTestResult from "@/components/school-shared/SpellingTestResult.vue";
import {
  SfSpellingTestResult,
  SfSpellingTestResultWord,
} from "@/types/spellingTypes";
import {
  spellingActivitySaveData,
  spellingActivityLoadedWords,
  spellingActivityResultId,
} from "@/state/spellingGameState";
import {
  SfSpellingTestAnswerWord,
  SfSpellingTestScoreDetails,
} from "@/types/spellingTypes";
import { spellingActivitySaveStatus } from "@/state/spellingGameState";
import { SfTestResultInfo } from "@/types/generalTypes";
import { SfActivitySaveData } from "@/types/sfActivityTypes";
import StudentFinalScreen from "./StudentFinalScreen.vue";
import getUtcDateTime from "@/func/format/getUtcDateTime";

export default defineComponent({
  components: { GuestFinalScreen, SpellingTestResult, StudentFinalScreen },
  emits: ["close"],
  data() {
    return {
      guestName: "",
      resultScreenVisible: false,
    };
  },
  computed: {
    tmpStatus() {
      return spellingActivitySaveStatus.value;
    },
    isGuest(): boolean {
      return this.$currentUser.value === null;
    },
    showResults(): boolean {
      if (this.isGuest && this.guestName == "") {
        return false;
      }
      return (
        spellingActivitySaveStatus.value == 2 &&
        spellingActivitySaveData.value?.mode == "test"
      );
    },
    saveData(): SfActivitySaveData | null {
      return spellingActivitySaveData.value;
    },
    resultData(): SfSpellingTestResult | null {
      if (!this.saveData) {
        return null;
      }
      const createdon = getUtcDateTime();
      return {
        id: spellingActivityResultId.value ? spellingActivityResultId.value : 0,
        student_id: 0,
        mode: this.saveData.mode,
        test_id: this.saveData.testId,
        word_category_id: this.saveData.wordCategoryId,
        word_list_id: this.saveData.wordListId,
        tricky_words: this.saveData.trickyWords,
        activity_id: this.saveData.activityId,
        game_id: this.saveData.gameId,
        code: this.saveData.code,
        createdon: createdon,
        sf_points: this.saveData.sfPoints,
        scoreDetails: this.buildScoreDetails(this.saveData.words),
        resultData: this.buildResultData(this.saveData.words),
      };
    },
    resultInfo(): SfTestResultInfo {
      const res = {} as SfTestResultInfo;
      if (this.saveData?.wordCategoryId) {
        res.wordCategoryId = this.saveData.wordCategoryId;
      }
      if (this.saveData?.testId) {
        res.testId = this.saveData.testId;
      }
      return res;
    },
    resultStyle(): any {
      const scaleS = "scale(" + 0.8 + ")";
      return {
        "transform-origin": "top left",
        MsZoom: 0.8,
        MozTransform: scaleS,
        OTransform: scaleS,
        WebkitTransform: scaleS,
        transform: scaleS,
      };
    },
  },
  methods: {
    buildScoreDetails(
      data: SfSpellingTestAnswerWord[],
    ): SfSpellingTestScoreDetails {
      const res = { a1: 0, a2: 0, a3: 0, tot: 0, wrong: 0 };
      for (let w of data) {
        for (let i in w.attempts) {
          if (w.attempts[i].is_correct) {
            switch (i) {
              case "0":
                res.a1++;
                break;
              case "1":
                res.a2++;
                break;
              case "2":
                res.a3++;
                break;
            }
          } else if (i == "2") {
            res.wrong++;
          }
          res.tot++;
        }
      }
      return res;
    },
    buildResultData(
      data: SfSpellingTestAnswerWord[],
    ): SfSpellingTestResultWord[] {
      const words = spellingActivityLoadedWords.value;
      const res = [] as SfSpellingTestResultWord[];
      if (!words) {
        return res;
      }
      for (let w of data) {
        const word = words.find((q) => q.id == w.wordId);
        if (word) {
          res.push({
            word,
            attempts: w.attempts,
          });
        }
      }
      return res;
    },
  },
});
</script>
